import React, { useEffect, useMemo } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useInView } from 'react-intersection-observer'
import useOpenRight from 'refactor/hooks/useOpenRight'
import PanelLoadingIndicator from './PanelLoadingIndicator'
import { RadioArtist, artistsUrl, formatPlays, formatSongs } from './radioArtistsModel'
import {
    ARTISTS_GC_MS,
    ARTISTS_STALE_MS,
    ArtistAvatar,
    ArtistMoney,
    getJson,
} from './radioArtistsParts'
import { useMusicPanelView } from './musicPanelView'
import styles from './radio-artists.module.scss'

type ArtistsPage = { artists: RadioArtist[]; has_more: boolean }

export default function RadioArtists({ panelId }: { panelId?: string }) {
    const { order } = useMusicPanelView(panelId)
    const { openRight } = useOpenRight(panelId)

    // Each order is its own list; scrolling near the end loads the next page.
    const query = useInfiniteQuery<ArtistsPage>({
        queryKey: ['radioArtists', order],
        staleTime: ARTISTS_STALE_MS,
        gcTime: ARTISTS_GC_MS,
        refetchOnMount: true,
        initialPageParam: 1,
        getNextPageParam: (lastPage, pages) => (lastPage?.has_more ? pages.length + 1 : undefined),
        queryFn: ({ pageParam }) => getJson(artistsUrl(order, pageParam as number)),
    })

    // Ranks shift between page fetches, so offset paging can repeat an artist
    // on a later page; keep the first.
    const artists = useMemo(() => {
        const seen = new Set<string>()
        return (query.data?.pages || [])
            .flatMap(page => page?.artists || [])
            .filter(artist => !seen.has(artist.id) && !!seen.add(artist.id))
    }, [query.data?.pages])

    const { ref: moreRef, inView } = useInView({ rootMargin: '0px 0px 600px 0px' })
    const { hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage } = query
    // A failed page waits for Retry instead of refetching while the sentinel stays in view.
    useEffect(() => {
        if (inView && hasNextPage && !isFetchingNextPage && !isFetchNextPageError) {
            void fetchNextPage()
        }
    }, [inView, hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage])

    return (
        <div className={styles.artists}>
            {query.isLoading && <PanelLoadingIndicator label="Loading artists" />}
            {query.isError && <div className={styles.empty}>Couldn't load artists.</div>}
            {query.isSuccess && artists.length === 0 && (
                <div className={styles.empty}>No artists have aired yet.</div>
            )}
            {artists.map((artist, index) => (
                <button
                    key={artist.id}
                    type="button"
                    className={styles.artistRow}
                    onClick={() => openRight({ user_music: artist.id })}
                >
                    <span className={styles.rank}>{artist.rank ?? index + 1}</span>
                    <ArtistAvatar artist={artist} />
                    <span className={styles.artistText}>
                        <span className={styles.artistName}>{artist.name || 'Artist'}</span>
                        <span className={styles.meta}>
                            <ArtistMoney sats={artist.upvalue_sats} /> ·{' '}
                            {formatPlays(artist.play_count)} · {formatSongs(artist.track_count)}
                        </span>
                    </span>
                </button>
            ))}
            {hasNextPage && <div ref={moreRef} aria-hidden="true" />}
            {isFetchingNextPage && <PanelLoadingIndicator label="Loading more artists" />}
            {isFetchNextPageError && !isFetchingNextPage && (
                <div className={styles.empty}>
                    Couldn't load more artists.{' '}
                    <button
                        type="button"
                        className={styles.retryButton}
                        onClick={() => void fetchNextPage()}
                    >
                        Retry
                    </button>
                </div>
            )}
        </div>
    )
}
